import * as React from 'react'
import { Box, Button, Container, Typography } from '@material-ui/core'
import { BrowserRouter } from 'react-router-dom'
import { clearAuthTokens } from 'axios-jwt'
import withTheme from './theme'
import NavBar from './nav'
import { makeStyles } from '@material-ui/styles'
import ApplicationStatusProvider from './common/applicationProgress/ApplicationProgressProvider'
import { UseSnackbarUI } from '@jetkit/react'
import GeneralOptionsProvider from './common/generalOptionsProvider'
import Routes from './routes'
import { useStoreState, useStoreActions, persistor } from './store'
import { reloadMe } from './services/userService'
import useRouter from 'use-react-router'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import { StripeProvider } from 'react-stripe-hooks'
import BillingTypeProvider from './common/billingType/BillingTypeProvider'
import { ErrorBoundary } from 'react-error-boundary'
import ReactGA from 'react-ga'
import { useApiErrorInterceptor } from './hooks/useApiErrorInterceptor'
import { hasLiveRefreshToken } from './services/session'
//react-stripe-hooks - git+https://github.com/jetbridge/react-stripe-hooks.git
type StripeT = any

// FIXME: we shouldn't have to declare this - it's already in @types/react-stripe-elements
declare global {
  interface Window {
    Stripe: (publicKey: string, options?: any | undefined) => StripeT
  }
}

ReactGA.initialize(process.env.REACT_APP_TRACKING_ID)

const useStyles = makeStyles({ content: {}, navContainer: { paddingBottom: 80 } })

const App: React.FC = () => {
  const classes = useStyles()
  const stateUser = useStoreState((state) => state.user.current)
  const setCurrentUser = useStoreActions((actions) => actions.user.setCurrent)
  const removeCurrentUser = useStoreActions((actions) => actions.user.removeCurrent)
  const [stripe, setStripe] = React.useState<StripeT | null>(null)
  const { history } = useRouter()

  // installs the 401 handler and ejects it on unmount
  useApiErrorInterceptor()

  // Drop a persisted session whose tokens have already lapsed. The store lives in
  // IndexedDB and never expires while the refresh token does (30 days), so a user coming
  // back after a break arrived holding one without the other.
  //
  // PrivateRoute and PublicRoute are what actually keep them out -- they check the same
  // thing during render, which this cannot, running after it. This is the cleanup: it
  // stops the stale name sitting in the nav, and stops reloadMe() below sending a
  // request that is certain to 401 and sign them out a second time.
  React.useEffect(() => {
    if (hasLiveRefreshToken()) return
    removeCurrentUser()
    clearAuthTokens()
  }, [removeCurrentUser])

  // deal with async loading of stripe.js
  // https://www.npmjs.com/package/react-stripe-elements#loading-stripejs-asynchronously
  React.useEffect(() => {
    if (!window || !document) return

    const stripeApiKey = process.env.REACT_APP_STRIPE_KEY_PUBLISHABLE || 'unconfigured'
    if (window.Stripe) {
      setStripe(window.Stripe(stripeApiKey))
    } else {
      const ele = document.querySelector('#stripe-js')
      if (!ele) {
        console.warn('Failed to load stripe.js')
        return
      }

      ele.addEventListener('load', () => {
        // Create Stripe instance once Stripe.js loads
        setStripe(window.Stripe(stripeApiKey))
      })
    }
  }, [])

  React.useEffect(() => {
    ;(async () => {
      // load latest user info from API if we're logged in
      if (!hasLiveRefreshToken() || !stateUser || !stateUser.email) return

      // load user and save
      const me = await reloadMe()
      if (!me) return
      setCurrentUser(me)
      // if profile isn't finished and user type is client - send them to signup step two
    })()

    // don't call this again if currentUser changes to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, setCurrentUser]) //

  // This boundary wraps the nav as well as the routes, so when it catches, the Log Out
  // button goes with it -- and the store is persisted, so a reload rehydrated straight
  // back into the same broken state. Both ways out are offered here.
  const signOutAndReload = React.useCallback(async () => {
    removeCurrentUser()
    clearAuthTokens()
    try {
      await persistor.purge()
    } catch {
      // if purging fails the hard navigation below still gets them off this screen
    }
    // A hard navigation rather than history.push: we are inside a failed render, and this
    // guarantees the store is built from scratch instead of rehydrated.
    window.location.assign('/sign-in')
  }, [removeCurrentUser])

  function ErrorFallback({ error, resetErrorBoundary }) {
    return (
      <Box display="flex" flexDirection="column" height="100vh" justifyContent="center">
        <Container maxWidth="md">
          <Typography align="center" color="textPrimary" variant="h4">
            Something went wrong:
          </Typography>
          <Typography align="center" color="textPrimary" variant="subtitle2">
            {error.message}
          </Typography>
          <Box display="flex" justifyContent="center" mt={3} style={{ gap: 12 }}>
            <Button variant="outlined" color="primary" onClick={resetErrorBoundary}>
              Try again
            </Button>
            <Button variant="contained" color="primary" onClick={signOutAndReload}>
              Sign out and start over
            </Button>
          </Box>
        </Container>
      </Box>
    )
  }

  return (
    <React.Fragment>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <UseSnackbarUI />
        <StripeProvider stripe={stripe}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <ApplicationStatusProvider>
              <BillingTypeProvider>
                <GeneralOptionsProvider>
                  <BrowserRouter>
                    <div className={classes.navContainer}>
                      <NavBar />
                    </div>
                    <div className={classes.content}>
                      <Routes />
                    </div>
                  </BrowserRouter>
                </GeneralOptionsProvider>
              </BillingTypeProvider>
            </ApplicationStatusProvider>
          </MuiPickersUtilsProvider>
        </StripeProvider>
      </ErrorBoundary>
    </React.Fragment>
  )
}

export default withTheme(App)
